import { ajax } from '../utils/apiUtils';
import { getDataAll } from '../utils/dataUtils';
import { insertHtmlWithScripts, showInputError } from '../utils/domUtils';
import { appendFormData, validateForm } from '../utils/formUtils';
import { SendData } from '../utils/SendData';
import { COMPONENT_TYPE } from '../constants';
import { assign } from '../utils/arrayUtils';

// Sub-modules
import { makeDraggable, onMouseDown, onMouseMove, onMouseUp } from './ModalManager/modalDrag';
import { makeResizable, onResizeMouseMove, onResizeMouseUp } from './ModalManager/modalResize';
import { setModalBodyScroll, createMask, removeMask, loadBody, unLoadBody, initBackdropObserver } from './ModalManager/modalBody';
import { updateAllModalsZIndex } from './ModalManager/modalZIndex';
import { initObservers, setAutofocusInternal } from './ModalManager/modalInit';

/**
 * ModalManager - универсальный менеджер для модальных окон и канвасов
 * 
 * По умолчанию открывает модальные окна (type='modal')
 * Для открытия канваса установите type='canvas'
 * 
 * Примеры использования:
 * 
 * // Открыть модальное окно (по умолчанию)
 * const modal = new ModalManager({ url: '/api/modal', data: {...} });
 * modal.open();
 * 
 * // Открыть канвас
 * const canvas = new ModalManager({ url: '/api/canvas', data: {...}, type: 'canvas' });
 * canvas.open();
 * 
 * Примечание: 
 * - Перетаскивание и изменение размера работает только для модальных окон
 * - Размеры модального окна сохраняются в localStorage по ключу modalSize_{id}
 * - Нельзя уменьшить размер ниже начальных размеров окна (переданных через $width/$height)
 * - При всех запросах автоматически добавляется заголовок X-Component-Type: 'modal' или 'canvas'
 */
export class ModalManager {
  // Статические свойства для z-index значений
  static BASE_MODAL_Z_INDEX = 1055;
  static BASE_BACKDROP_Z_INDEX = 1050;
  static Z_INDEX_INCREMENT = 10;

  // Resize: порог определения края (px) и карта курсоров
  static RESIZE_EDGE = 5;
  static RESIZE_CURSORS = {
    n: 'ns-resize', s: 'ns-resize',
    e: 'ew-resize', w: 'ew-resize',
    ne: 'nesw-resize', sw: 'nesw-resize',
    nw: 'nwse-resize', se: 'nwse-resize',
  };

  // Статический счетчик открытых модальных окон
  static activeModals = [];

  constructor({ elm = null, elem = null, url = '', response = null, data = {}, loadBody = null, parentModal = null, log = false, onCloseModalResolve = false, sendFormDataOnRelaod = false, onOpen = null, fullscreen = false, noElemData = false, type = 'modal', redirect = false } = {}) {

    this._headers = {};
    this.element = elem ?? elm;
    this.closeData = {};
    this.data = Object.assign(noElemData ? {} : getDataAll(this.element), data);
    if (redirect) {
      url = Redirect.route(redirect, this.data);
    }
    this.url = url;
    this.modal = null;
    this.form = null;
    this.fieldset = null;
    this._existingManager = null;
    this.modalElement = null;
    this.modalDialog = null;
    this.originalContent = null;
    this.disableValidate = false;
    this.resolve = null;
    this.reject = null;
    this.mask = null;
    this.fullscreen = fullscreen;
    this.log = log;
    this._loadBody = loadBody;
    this.response = response;
    this.parentModal = parentModal;
    this.sendDataPromice = null;
    this.forceClose = false;
    this._onOpen = onOpen;
    this._customValidate = null;
    this._onCloseModalResolve = onCloseModalResolve;
    this._sendFormDataOnRelaod = sendFormDataOnRelaod;
    this.urlPath = new URL(this.url, window.location.origin).pathname; // Извлекаем путь из URL
    this.type = type; // 'modal' или 'canvas'

    // Для перетаскивания (только для модалок)
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.dragOffsetX = 0;
    this.dragOffsetY = 0;

    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);

    // Для изменения размера (только для модалок)
    this._onResizeMouseMove = this._onResizeMouseMove.bind(this);
    this._onResizeMouseUp = this._onResizeMouseUp.bind(this);
    this._resizeDirection = null;
    this._resizeStartX = 0;
    this._resizeStartY = 0;
    this._resizeStartWidth = 0;
    this._resizeStartHeight = 0;
    this._resizeStartTransformX = 0;
    this._resizeStartTransformY = 0;
    this._minWidth = 0;
    this._minHeight = 0;
    this._resizeListenersAttached = false;
    this._isResizing = false;

    this._inputEdit = {};
  }

  _updateSelf() {
    if (this._existingManager) Object.assign(this, this._existingManager.modalElement.modalManager);
  }

  async _init() {
    this.relatedData = null;
    // Привязываем экземпляр класса к DOM элементу модального окна
    if (this.modalElement) {
      this.modalElement.modalManager = this;
      this.modalElement.reloader = this.updateData.bind(this);
      this.modalElement.closer = this.closeModal.bind(this);
      if (this.element) {
        const parentModal = this.element.closest('.modal');
        if (parentModal) this.parentModal = this.element.closest('.modal').modalManager;
      }
    }

    initObservers(this);
  }

  _setFocus() {
    // Делегировано в modalInit.js через initObservers
  }

  setAutofocus() {
    this._setAutofocus();
  }

  _setAutofocus() {
    setAutofocusInternal(this);
  }

  _makeDraggable() {
    makeDraggable(this);
  }

  _onMouseDown(event) {
    onMouseDown(this, event);
  }

  _onMouseMove(event) {
    onMouseMove(this, event);
  }

  _onMouseUp() {
    onMouseUp(this);
  }

  _makeResizable() {
    makeResizable(this);
  }

  _onResizeMouseMove(event) {
    onResizeMouseMove(this, event);
  }

  _onResizeMouseUp() {
    onResizeMouseUp(this);
  }

  _setModalBodyScroll() {
    setModalBodyScroll(this);
  }

  _removeMask() {
    removeMask(this);
  }

  _createMask() {
    createMask(this);
  }

  open() {
    if (this.url === null || this.url === '') {
      return Promise.reject(new Error('URL is not set'));
    }
    return (this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      this._createMask();
      // console.log(this);
      let formData = new FormData();

      if (this.data._FILES) {
        for (const [key, fileList] of Object.entries(this.data._FILES)) {
          Array.from(fileList).forEach((file, index) => {
            formData.append(`${key}[${index}]`, file);
          });
        }
        delete this.data._FILES;
      }

      appendFormData(formData, this.data);

      ajax({
        type: 'POST',
        url: this.url,
        data: formData,
        processData: false,
        contentType: false,
        headers: {
          [COMPONENT_TYPE]: this.type
        },
        success: (response, status, responseData) => {
          this.response = response;
          if (responseData.headers.get('Content-Type')?.includes('application/json')) {
            throw new Error('Ожидался HTML, но получен JSON. Проверьте правильность URL и возвращаемого ответа от сервера.');
          }
          this.renderModal();
        },
        error: (xhr, status, error, responseData) => {
          console.error(xhr);

          this.response = responseData;
          window.MessageManager.processResponse(responseData);
          this._removeMask();
          this.clearResponse().renderModal();
        }
      });
    }));
  }

  modalLoadedEvent() {
    const modalLoadedEvent = new CustomEvent(this.modalLoaded ? 'modalReloaded' : 'modalLoaded', {
      detail: { target: this.modalElement },
      bubbles: true,
      cancelable: true,
      target: this.modalElement
    });
    this.modalLoaded = true;

    this.modalElement.dispatchEvent(modalLoadedEvent);
  }

  clearResponse(error = true) {
    const responseText = typeof this.response === 'object' ? this.response.answer : this.response;

    // Определяем паттерн в зависимости от типа
    const pattern = this.type === 'canvas'
      ? (error ? /<div class="offcanvas/ : /<div class="offcanvas/)
      : (error ? /<div class="modal fade" id="errorModal"/ : /<div class="modal fade"/);

    const modalStartIndex = responseText.search(pattern);

    if (modalStartIndex !== -1) {
      const cleanedResponseText = responseText.substring(modalStartIndex);
      this.response = cleanedResponseText;
    } else {
      this.response = responseText;
    }
    return this;
  }

  setValidateForm(validate) {
    this._customValidate = validate;
  }

  setElements() {
    if (this.type === 'canvas') {
      this.modalDialog = this.modalElement.querySelector('.offcanvas-dialog');
    } else {
      this.modalDialog = this.modalElement.querySelector('.modal-dialog');
    }
    this.form = this.modalElement.querySelector('form');
    this.fieldset = this.modalElement.querySelector('fieldset');

    this.fieldsetKeys = this.fieldset ? Object.keys(this.fieldset.data || {}) : [];

    if (!this?.fieldset?.updateFileInputs) return;
    this.fieldset.updateFileInputs(this.data._FILES);
    // Обновляем this.data на основе fieldset после установки файлов
    this.data = Object.assign(this.data, this.fieldset.data);
  }

  _initBackdropObserver() {
    initBackdropObserver(this);
  }

  renderModal() {
    return (this.promise = new Promise((resolve, reject) => {
      if (!this.resolve) this.resolve = resolve;
      if (!this.reject) this.reject = reject;
      const container = document.querySelector('.fullScreenElement') ?? document.body;

      this._removeMask();
      this.clearResponse(false);
      if (this.forceClose) return;

      insertHtmlWithScripts({
        parent: container,
        html: this.response
      });

      // Ищем элемент в зависимости от типа
      const selector = this.type === 'canvas' ? '.offcanvas' : '.modal.fade';
      const elements = container.querySelectorAll(selector);
      if (elements.length === 0) throw `Нет ${this.type === 'canvas' ? 'канваса' : 'модального окна'}`;
      this.modalElement = elements[elements.length - 1];

      if (this.fullscreen && this.type === 'modal') {
        this.modalElement.classList.add('fullscreen');
      }

      this.setElements();

      // Создаем экземпляр в зависимости от типа
      if (this.type === 'canvas') {
        this.modal = new bootstrap.Offcanvas(this.modalElement, {
          keyboard: true,
          backdrop: true,
          scroll: false
        });
      } else {
        this.modal = new bootstrap.Modal(this.modalElement, {
          keyboard: true,
          backdrop: 'static'
        });
      }

      this._init();

      if (document.querySelector('.fullScreenElement')) {
        this._initBackdropObserver();
      }

      let mouseDownTarget = null;
      this.modalElement.addEventListener('mousedown', (e) => {
        mouseDownTarget = e.target;
      });

      this.modalElement.addEventListener('click', (event) => {
        // Закрытие при клике на фон (только для модалок, не во время resize)
        if (this.type === 'modal' && !this._isResizing && event.target === this.modalElement && mouseDownTarget === this.modalElement) {
          this.reject();
          this.closeModal();
          return;
        }

        if (!event.target.closest('[data-my-dismiss="modal"]')) return;
        this._sendData({
          event,
          targetClick: event.target.closest('[data-my-dismiss="modal"]')
        });
      });

      // Обработка закрытия по Escape
      this.modalElement.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.reject();
          this.closeModal();
        }
      });

      const hiddenEvent = this.type === 'canvas' ? 'hidden.bs.offcanvas' : 'hidden.bs.modal';
      this.modalElement.addEventListener(hiddenEvent, this.cleanup.bind(this));

      this.modal.show();

      // Добавляем в статический массив (для всех типов)
      ModalManager.activeModals.push(this);

      // Обновляем z-index для всех модальных окон
      ModalManager.updateAllModalsZIndex();

      this.adjustZIndexForModals();
      this.data = Object.assign(this.data, getDataAll(this.modalElement.querySelector('fieldset')));

      const shownEvent = this.type === 'canvas' ? 'shown.bs.offcanvas' : 'shown.bs.modal';
      this.modalElement.addEventListener(
        shownEvent,
        () => {
          Promise.resolve()
            .then(() => {
              if (this._onOpen) {
                this._onOpen();
              }
            })
            .then(() => this.modalLoadedEvent())
            .then(() => {
              // Устанавливаем фокус на autofocus элемент после полного открытия
              this._setAutofocus();
            })
            .then(() => this._setModalBodyScroll())
            .then(() => {
              // Resize инициализируется один раз после полного открытия,
              // когда modal-content уже имеет реальные размеры
              if (this.type === 'modal') {
                this._makeResizable();
              }
            });
        },
        { once: true }
      );
    }));
  }

  adjustZIndexForModals() {
    ModalManager.updateAllModalsZIndex();
  }

  async _sendData({ event, targetClick }) {
    console.log('_sendData');
    const dismisTarget = targetClick;
    const target = event.target;
    const relatedData = getDataAll(dismisTarget);
    // Проверяем, если элемент имеет специальный атрибут для предотвращения закрытия
    if (targetClick.closest('[data-my-dismiss="stopDismiss"]')) return;

    // console.log('_sendData', dismisTarget);
    if (dismisTarget.dataset.myType === 'sendData') {
      if (!(await this._validateForm(targetClick))) return; // Вызываем метод валидации
      if (dismisTarget.dataset.myEvent === 'prompt') {
        relatedData.prompt = prompt('Количество контактов у компонента');
        if (!relatedData.prompt) return;
      }

      // console.log('[data-my-dismiss="modal"]', relatedData, event);
      // console.log(relatedData);
      if (this.sendDataPromice) {
        this.sendDataPromice(relatedData);
      } else this.resolve(Object.assign({}, relatedData, this.closeData));
    } else {
      // Закрытие без отправки данных - просто reject
      this.reject();
    }

    this.closeModal(); // Закрываем модальное окно
  }

  async _validateForm(elem) {
    // debugger;
    return await validateForm.bind(this)({ elem, customValidate: this._customValidate, disableValidate: this.disableValidate });
  }

  async closeModal(response = {}) {
    document.activeElement.blur();
    if (this.modalclosed) return this.modalclosed;
    this.modalclosed = true;
    this.closeData = response?.answer ?? response?.data ?? response ?? {};
    if (this.modal) {
      this.modal.hide();

      const shownEvent = this.type === 'canvas' ? 'shown.bs.offcanvas' : 'shown.bs.modal';
      this.modalElement.addEventListener(shownEvent, () => {
        this.modal.hide();
      });

      // Ждем, пока элемент не скроется полностью (display: none)
      while (this.modalElement && this.modalElement.offsetParent !== null) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return true;
    }
  }

  cleanup() {
    if (!this.modalElement) return;

    // Удаляем модальное окно из статического массива
    const index = ModalManager.activeModals.indexOf(this);
    if (index > -1) {
      ModalManager.activeModals.splice(index, 1);
    }

    // Обновляем z-index для оставшихся модальных окон
    ModalManager.updateAllModalsZIndex();

    if (this._sendFormDataOnRelaod) {
      this.fieldset?.updateData();
      this.data = Object.assign(this.data, this.fieldset?.data ?? {});
    }
    // Очищаем обработчики ресайза
    document.removeEventListener('mousemove', this._onResizeMouseMove);
    document.removeEventListener('mouseup', this._onResizeMouseUp);
    this._resizeListenersAttached = false;
    this._isResizing = false;

    if (this._onCloseModalResolve) this.resolve(Object.assign({}, this.data, this.closeData));
    this.modalElement.remove();
    this.modalElement = null;
    this.modal = null;
  }

  // Статический метод для обновления z-index всех модальных окон
  static updateAllModalsZIndex() {
    updateAllModalsZIndex(ModalManager);
  }

  reloadForm() {
    this.fieldset.updateData();
    this.data = Object.assign(this.data, this.fieldset.data);
    // Проверяем, если какой-либо ключ из this.fieldsetKeys пропал в Object.keys(this.fieldset.data)
    const currentKeys = Object.keys(this.fieldset.data || {});
    this.fieldsetKeys.forEach((key) => {
      if (!currentKeys.includes(key)) {
        delete this.data[key]; // Удаляем ключ из this.data, если он отсутствует в текущих данных fieldset
      }
    });
    if (this.modal) {
      this.loadBody();
      const headers = {
        [COMPONENT_TYPE]: this.type
      };
      new SendData({ data: this.data, url: this.url, uuidKey: this.uuidKey, headers }).then((response) => {
        debugger;
        this.updateModal(response.answer);
      });
    }
  }

  async updateData({ data = {}, options, trigerElement = null } = {}) {
    const buttons = this.modalElement.querySelectorAll('.btn');
    buttons.forEach((btn) => {
      btn.disabled = true;
    });
    if (trigerElement && !(await this._validateForm(trigerElement))) return;
    if (this._sendFormDataOnRelaod) {
      this._headers['Reload'] = 'true';
      this.fieldset.updateData();
      this.data = Object.assign(this.data, this.fieldset.data);
      const currentKeys = Object.keys(this.fieldset.data || {});
      this.fieldsetKeys.forEach((key) => {
        if (!currentKeys.includes(key)) {
          delete this.data[key]; // Удаляем ключ из this.data, если он отсутствует в текущих данных fieldset
        }
      });
    }
    this._headers[COMPONENT_TYPE] = this.type;
    this.data = assign({
      objects: [this.data, data],
      options: {
        mode: options?.options ?? 'mergeUpdate',
        arrayStrategy: options?.arrayStrategy ?? 'merge',
        conflictStrategy: options?.conflictStrategy ?? 'replace',
        preserveExisting: options?.preserveExisting ?? false,
        deleteMissing: options?.deleteMissing ?? false,
        deep: options?.deep ?? true,
        maxDepth: options?.maxDepth ?? Infinity,
        circular: options?.circular ?? 'replace',
        specialTypes: options?.specialTypes ?? {},
        _reverse: options?._reverse ?? false
      }
    });
    if (this.log) console.log('updateData:', this.data);
    if (this.modal) {
      this.loadBody();
      await new SendData({ data: this.data, url: this.url, uuidKey: this.uuidKey, headers: this._headers }).then((response) => {
        this.updateModal(response.answer);
      });
    }
  }

  loadBody() {
    loadBody(this);
  }

  unLoadBody() {
    unLoadBody(this);
  }

  updateModal(response) {
    if (this.modal) {
      this.response = response;
      this.clearResponse(false);
      if (this.log) console.log(this.modalElement);

      // Сохраняем текущую вертикальную прокрутку
      const scrollContainer = this.type === 'canvas'
        ? this.modalElement.querySelector('.offcanvas-body')
        : this.modalElement.querySelector('.modal-body');
      const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

      this.modalDialog.remove();
      insertHtmlWithScripts({
        html: this.response,
        parent: this.modalElement,
        selector: this.type === 'canvas' ? '.offcanvas' : '.modal'
      });

      // Восстанавливаем сохранённую прокрутку
      requestAnimationFrame(() => {
        const newScrollContainer = this.type === 'canvas'
          ? this.modalElement.querySelector('.offcanvas-body')
          : this.modalElement.querySelector('.modal-body');
        if (newScrollContainer) {
          newScrollContainer.scrollTop = scrollTop;
        }

        this.setElements();
        // После обновления содержимого пересчитываем высоты body (для modal и offcanvas)
        this._setModalBodyScroll();

        // Изменение размера после обновления modalDialog (только для модалок)
        if (this.type === 'modal') {
          this._makeResizable();
        }
      });


      // Перетаскивание только для модалок
      if (this.type === 'modal') {
        this._makeDraggable();
      }

      // Устанавливаем автофокус после обновления содержимого
      setTimeout(() => this._setAutofocus(), 100);
    }
  }
}
